import { useEffect } from 'react';
import type React from 'react';

/**
 * Keeps ancestor accordion containers in sync with dynamic content height changes.
 * @hook
 *
 * @param {React.RefObject<HTMLElement | null>} containerRef - Ref to the content container rendered inside accordion panels.
 * @returns {void} Does not return a value; synchronizes ancestor accordion heights as content changes.
 */
export const useAccordionHeightSync = (containerRef: React.RefObject<HTMLElement | null>) => {
  useEffect(() => {
    const containerEl = containerRef.current;
    const MAX_PARENT_TRAVERSAL_DEPTH = 100;

    if (!containerEl) {
      return;
    }

    let frameId: number | null = null;

    const updateAncestorAccordionHeights = () => {
      try {
        let parentEl: Element | null = containerEl.parentElement;
        let traversedParents = 0;

        while (parentEl && traversedParents < MAX_PARENT_TRAVERSAL_DEPTH) {
          if (!(parentEl instanceof HTMLElement)) {
            break;
          }

          const inlineMaxHeight = parentEl.style.maxHeight;

          if (inlineMaxHeight) {
            parentEl.style.maxHeight = `${parentEl.scrollHeight}px`;
          }

          parentEl = parentEl.parentElement;
          traversedParents += 1;
        }
      } catch {
        // no-op
      }
    };

    const scheduleHeightUpdate = () => {
      if (frameId !== null) {
        return;
      }

      frameId = globalThis.requestAnimationFrame(() => {
        frameId = null;
        updateAncestorAccordionHeights();
      });
    };

    scheduleHeightUpdate();

    const resizeObserver = new ResizeObserver(() => {
      scheduleHeightUpdate();
    });

    resizeObserver.observe(containerEl);

    const mutationObserver = new MutationObserver(() => {
      scheduleHeightUpdate();
    });

    mutationObserver.observe(containerEl, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    return () => {
      if (frameId !== null) {
        globalThis.cancelAnimationFrame(frameId);
      }

      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [containerRef]);
};
